import { Link, useParams } from 'react-router'
import { format, formatDistanceToNow } from 'date-fns'
import { ArrowLeft, Box, Building2, ChevronRight, Copy, Layout as LayoutIcon } from 'lucide-react'
import { toast } from 'sonner'
import { useAdminLocationDetail } from '@/hooks/useAdminLocations'
import { formatSceneCounts } from '@/lib/layoutRequirements'
import { PageHeader } from '@/components/shared/PageHeader'
import { ExportJsonMenu } from '@/components/shared/ExportJsonMenu'
import { ThumbnailPreview } from '@/components/shared/ThumbnailPreview'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

export function LocationDetailPage() {
  const { locationId } = useParams<{ locationId: string }>()
  const { data, isLoading } = useAdminLocationDetail(locationId)

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    )
  }

  if (!data) {
    return <p className="text-muted-foreground">Location not found.</p>
  }

  const { location, owner, layouts } = data
  const isDeleted = !!location.deleted_at

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied`)
  }

  return (
    <div>
      <div className="mb-4">
        <Link
          to="/locations"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Locations
        </Link>
      </div>

      <PageHeader
        title={location.name}
        media={
          <ThumbnailPreview
            src={location.thumbnail_url}
            alt={location.name}
            kind="location"
            className="h-14 w-20"
            iconClassName="h-6 w-6"
          />
        }
        description={
          location.airport_code
            ? `${location.airport_code} · ${location.airport_name ?? ''}`.trim()
            : (location.address ?? 'No airport or address on file')
        }
        actions={
          <div className="flex gap-2">
            {isDeleted && <Badge variant="destructive">Deleted</Badge>}
            <ExportJsonMenu locationId={location.id} />
          </div>
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Metadata</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Location ID">
              <code className="text-xs">{location.id.slice(0, 12)}…</code>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => copy(location.id, 'Location ID')}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </Row>
            <Row label="Coordinates">
              {Number(location.latitude).toFixed(5)}, {Number(location.longitude).toFixed(5)}
            </Row>
            <Row label="Airport">
              {location.airport_code
                ? `${location.airport_code}${location.airport_name ? ` — ${location.airport_name}` : ''}`
                : '—'}
            </Row>
            <Row label="Address">{location.address || '—'}</Row>
            <Row label="Slug">{location.slug || '—'}</Row>
            <Row label="Layouts">{location.layout_count}</Row>
            <Row label="Created">{format(new Date(location.created_at), 'MMM d, yyyy HH:mm')}</Row>
            <Row label="Updated">
              {formatDistanceToNow(new Date(location.updated_at), { addSuffix: true })}
            </Row>
            {isDeleted && (
              <Row label="Deleted">
                {format(new Date(location.deleted_at as string), 'MMM d, yyyy HH:mm')}
              </Row>
            )}
          </CardContent>
        </Card>

        {/* Owner + provenance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Owner & provenance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Email">{owner.email || '—'}</Row>
            <Row label="Display Name">{owner.display_name || '—'}</Row>
            <Row label="Tier">
              <Badge variant="outline" className="capitalize">
                {owner.tier.replace('_', ' ')}
              </Badge>
            </Row>
            <Row label="User ID">
              <Link to={`/users/${owner.id}`} className="text-primary hover:underline">
                Open user
              </Link>
            </Row>
            <Row label="Source listing">
              {location.source_listing_url ? (
                <a
                  href={String(location.source_listing_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {String(location.source_listing_id ?? 'listing')}
                </a>
              ) : (
                (String(location.source_listing_id ?? '') || '—')
              )}
            </Row>
            <Row label="Forked from">
              {location.forked_from_location_id ? (
                <Link
                  to={`/locations/${String(location.forked_from_location_id)}`}
                  className="text-primary hover:underline"
                >
                  {String(location.forked_from_location_id).slice(0, 8)}…
                </Link>
              ) : (
                '—'
              )}
            </Row>
          </CardContent>
        </Card>

        {/* Layouts */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <LayoutIcon className="h-4 w-4" /> Layouts ({layouts.length})
            </CardTitle>
            <CardDescription>
              Every layout at this location, including soft-deleted ones. All of them are in the
              location export.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {layouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No layouts at this location.</p>
            ) : (
              <ul className="divide-y">
                {layouts.map((layout) => (
                  <li key={layout.id}>
                    <Link
                      to={`/layouts/${layout.id}`}
                      className="flex items-center gap-3 py-3 transition-colors hover:bg-muted/50"
                    >
                      <ThumbnailPreview
                        src={layout.thumbnail_url}
                        alt={layout.name}
                        kind="layout"
                        className="h-11 w-16"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{layout.name}</span>
                          {layout.is_public && (
                            <Badge variant="secondary" className="text-[10px]">
                              Public
                            </Badge>
                          )}
                          {layout.deleted_at && (
                            <Badge variant="destructive" className="text-[10px]">
                              Deleted
                            </Badge>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="h-3 w-3" /> {formatSceneCounts(layout)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Box className="h-3 w-3" /> {layout.item_count} item
                            {layout.item_count !== 1 ? 's' : ''}
                          </span>
                          <span>rev {layout.revision}</span>
                          <span>
                            · {formatDistanceToNow(new Date(layout.updated_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-1 text-sm">{children}</div>
    </div>
  )
}
